import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "../components/Layout";
import {
  ChartBarIcon,
  UsersIcon,
  CpuChipIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  SparklesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ServerIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { getApiUrl, ADMIN_API_CONFIG } from "../config/api";
import { io, Socket } from "socket.io-client";

export const AdminDashboard: React.FC = () => {
  const { token } = useAuth();
  const [summary, setSummary] = useState<{
    totalUsers: number;
    activeSessions: number;
  } | null>(null);
  const [aiUsage, setAiUsage] = useState<{
    from: string;
    to: string;
    groupBy: "day" | "week" | "month";
    series: Array<{
      period: string;
      requests: number;
      totalTokens: number;
      promptTokens: number;
      completionTokens: number;
    }>;
    totals: {
      requests: number;
      totalTokens: number;
      promptTokens: number;
      completionTokens: number;
    };
  } | null>(null);
  const [groupBy, setGroupBy] = useState<"day" | "week" | "month">("day");
  const [from, setFrom] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState<string>(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [byUser, setByUser] = useState<
    Array<{
      userId: string | null;
      email: string | null;
      requests: number;
      totalTokens: number;
      promptTokens: number;
      completionTokens: number;
    }>
  >([]);
  const [overallStats, setOverallStats] = useState<{
    totals: {
      requests: number;
      totalTokens: number;
      promptTokens: number;
      completionTokens: number;
    };
    perUser: Array<{
      userId: string | null;
      email: string | null;
      requests: number;
      totalTokens: number;
      promptTokens: number;
      completionTokens: number;
    }>;
  } | null>(null);
  const [activities, setActivities] = useState<
    Array<{
      id: string;
      type: string;
      message: string | null;
      createdAt: string;
    }>
  >([]);

  useEffect(() => {
    const load = async () => {
      try {
        const fromDate = new Date(from);
        const toDate = new Date(to);
        const qs = new URLSearchParams({
          from: fromDate.toISOString(),
          to: toDate.toISOString(),
          groupBy,
        }).toString();
        const [sRes, aRes, uRes] = await Promise.all([
          fetch(getApiUrl("/api/admin/dashboard/summary"), {
            headers: { Authorization: token ? `Bearer ${token}` : "" },
          }),
          fetch(getApiUrl("/api/admin/dashboard/activities?limit=10"), {
            headers: { Authorization: token ? `Bearer ${token}` : "" },
          }),
          fetch(getApiUrl(`/api/admin/dashboard/ai-usage?${qs}`), {
            headers: { Authorization: token ? `Bearer ${token}` : "" },
          }),
        ]);
        if (sRes.ok) setSummary(await sRes.json());
        if (aRes.ok) setActivities(await aRes.json());
        if (uRes.ok) setAiUsage(await uRes.json());

        const byUserRes = await fetch(
          getApiUrl(
            `/api/admin/dashboard/ai-usage/by-user?from=${encodeURIComponent(fromDate.toISOString())}&to=${encodeURIComponent(toDate.toISOString())}`
          ),
          { headers: { Authorization: token ? `Bearer ${token}` : "" } }
        );
        if (byUserRes.ok) setByUser(await byUserRes.json());

        // Загружаем общую статистику
        const overallRes = await fetch(
          getApiUrl("/api/admin/dashboard/ai-usage/overall"),
          { headers: { Authorization: token ? `Bearer ${token}` : "" } }
        );
        if (overallRes.ok) setOverallStats(await overallRes.json());
      } catch {}
    };
    load();

    // WebSocket for realtime updates
    const socket: Socket = io(`${ADMIN_API_CONFIG.WS_BASE_URL}/admin`, {
      path: "/socket.io",
      transports: ["websocket", "polling"],
      withCredentials: true,
    });

    const handleActivity = (a: {
      id: string;
      type: string;
      message: string | null;
      createdAt: string;
    }) => {
      setActivities((prev) => {
        const next = [
          {
            id: a.id,
            type: a.type,
            message: a.message ?? null,
            createdAt: a.createdAt,
          },
          ...prev,
        ];
        return next.slice(0, 10);
      });
    };

    const handleSummaryChanged = async () => {
      try {
        const sRes = await fetch(getApiUrl("/api/admin/dashboard/summary"), {
          headers: { Authorization: token ? `Bearer ${token}` : "" },
        });
        if (sRes.ok) setSummary(await sRes.json());
      } catch {}
    };

    socket.on("admin:activity", handleActivity);
    socket.on("admin:summary_changed", handleSummaryChanged);

    return () => {
      socket.off("admin:activity", handleActivity);
      socket.off("admin:summary_changed", handleSummaryChanged);
      socket.disconnect();
    };
  }, [token, from, to, groupBy]);

  const groupByOptions = [
    { value: "day", label: "Дни" },
    { value: "week", label: "Недели" },
    { value: "month", label: "Месяцы" },
  ] as const;

  const usageSeries = useMemo(() => {
    if (!aiUsage) return [] as Array<{ x: string; y: number; y2: number }>;
    return aiUsage.series.map((p) => ({
      x: new Date(p.period).toLocaleDateString(),
      y: p.requests,
      y2: p.totalTokens,
    }));
  }, [aiUsage]);

  const stats = [
    {
      name: "Всего пользователей",
      value: summary ? String(summary.totalUsers) : "—",
      icon: UsersIcon,
      description: "Зарегистрированные пользователи",
      color: "blue",
      trend: summary?.totalUsers && summary.totalUsers > 0 ? "up" : "neutral",
    },
    {
      name: "Активные сессии",
      value: summary ? String(summary.activeSessions) : "—",
      icon: GlobeAltIcon,
      description: "Текущие онлайн сессии",
      color: "green",
      trend:
        summary?.activeSessions && summary.activeSessions > 0
          ? "up"
          : "neutral",
    },
    {
      name: "Админ сессии",
      value: summary ? String((summary as any).activeAdminSessions || 0) : "—",
      icon: ShieldCheckIcon,
      description: "Активные админы",
      color: "purple",
      trend: "neutral",
    },
    {
      name: "Системный статус",
      value: "Онлайн",
      icon: ServerIcon,
      description: "Состояние сервера",
      color: "emerald",
      trend: "up",
    },
  ];

  const recentActivities = activities.map((a) => ({
    id: a.id,
    action: a.message || a.type,
    time: new Date(a.createdAt).toLocaleString(),
    type: a.type,
  }));

  const getColorClasses = (color: string) => {
    const colorMap = {
      blue: {
        bg: "from-blue-50 to-blue-100",
        border: "border-blue-200",
        icon: "text-blue-600",
        text: "text-blue-700",
        value: "text-blue-900",
      },
      green: {
        bg: "from-green-50 to-green-100",
        border: "border-green-200",
        icon: "text-green-600",
        text: "text-green-700",
        value: "text-green-900",
      },
      purple: {
        bg: "from-purple-50 to-purple-100",
        border: "border-purple-200",
        icon: "text-purple-600",
        text: "text-purple-700",
        value: "text-purple-900",
      },
      emerald: {
        bg: "from-emerald-50 to-emerald-100",
        border: "border-emerald-200",
        icon: "text-emerald-600",
        text: "text-emerald-700",
        value: "text-emerald-900",
      },
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  const getTrendIcon = (trend: string) => {
    if (trend === "up")
      return <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />;
    if (trend === "down")
      return <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />;
    return null;
  };

  return (
    <Layout>
      {/* Stats */}
      <div>
        <div className="flex items-center gap-2 mb-6">
          <EyeIcon className="h-6 w-6 text-indigo-600" />
          <h2 className="text-xl font-semibold text-gray-900">Обзор системы</h2>
        </div>
        <div className="card">
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {stats.map((item) => (
                <div
                  key={item.name}
                  className="bg-white rounded-lg border border-gray-200 p-4"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-500 h-10 flex items-start pt-1 leading-tight">
                        {item.name}
                      </div>
                      <div className="text-2xl font-bold text-gray-900">
                        {item.value}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {item.description}
                      </div>
                    </div>
                    <div className="flex-shrink-0 ml-3">
                      <item.icon
                        className={`h-6 w-6 ${getColorClasses(item.color).icon}`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="mt-8">
        <div className="flex items-center gap-2 mb-6">
          <ClockIcon className="h-6 w-6 text-indigo-600" />
          <h2 className="text-xl font-semibold text-gray-900">
            Последние действия
          </h2>
        </div>
        <div className="card">
          <div className="px-6 py-4">
            {recentActivities.length === 0 ? (
              <div className="text-center py-8">
                <ClockIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Нет последних действий</p>
              </div>
            ) : (
              <div className="flow-root">
                <ul className="-mb-8">
                  {recentActivities.map((activity, activityIdx) => (
                    <li key={activity.id}>
                      <div className="relative pb-8">
                        {activityIdx !== recentActivities.length - 1 ? (
                          <span
                            className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                            aria-hidden="true"
                          />
                        ) : null}
                        <div className="relative flex space-x-3">
                          <div>
                            <span className="h-8 w-8 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center ring-4 ring-white shadow-sm">
                              <UsersIcon className="h-4 w-4 text-white" />
                            </span>
                          </div>
                          <div className="min-w-0 flex-1 pt-1.5">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900 mb-1">
                                  {activity.action}
                                </p>
                                <div className="flex items-center gap-2">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                    {activity.type}
                                  </span>
                                </div>
                              </div>
                              <div className="text-right">
                                <time className="text-xs text-gray-500 whitespace-nowrap">
                                  {activity.time}
                                </time>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AI Usage */}
      <div className="mt-8">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <SparklesIcon className="h-6 w-6 text-indigo-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Использование AI
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex items-center gap-1">
                <label className="text-sm text-gray-600 font-medium whitespace-nowrap">
                  От:
                </label>
                <input
                  type="date"
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent cursor-pointer w-full sm:w-auto"
                  style={{ colorScheme: "light" }}
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  max={to}
                  onClick={(e) => {
                    e.currentTarget.showPicker?.();
                  }}
                />
              </div>
              <div className="flex items-center gap-1">
                <label className="text-sm text-gray-600 font-medium whitespace-nowrap">
                  До:
                </label>
                <input
                  type="date"
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent cursor-pointer w-full sm:w-auto"
                  style={{ colorScheme: "light" }}
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  min={from}
                  onClick={(e) => {
                    e.currentTarget.showPicker?.();
                  }}
                />
              </div>
            </div>
            <div className="relative">
              <select
                className="appearance-none border border-gray-300 rounded-md pl-3 pr-10 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white cursor-pointer min-w-[140px] w-full sm:w-auto"
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as any)}
              >
                {groupByOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg
                  className="h-4 w-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Общая статистика */}
        {overallStats && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Общая статистика (все время)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-700">
                      Всего запросов
                    </p>
                    <p className="text-2xl font-bold text-blue-900">
                      {overallStats.totals.requests.toLocaleString()}
                    </p>
                  </div>
                  <div className="flex-shrink-0 ml-3">
                    <ChatBubbleLeftRightIcon className="h-6 w-6 text-blue-500" />
                  </div>
                </div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-700">
                      Всего токенов
                    </p>
                    <p className="text-2xl font-bold text-purple-900">
                      {overallStats.totals.totalTokens.toLocaleString()}
                    </p>
                  </div>
                  <div className="flex-shrink-0 ml-3">
                    <CpuChipIcon className="h-6 w-6 text-purple-500" />
                  </div>
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-700">
                      Промпт токены
                    </p>
                    <p className="text-2xl font-bold text-green-900">
                      {overallStats.totals.promptTokens.toLocaleString()}
                    </p>
                  </div>
                  <div className="flex-shrink-0 ml-3">
                    <ArrowTrendingUpIcon className="h-6 w-6 text-green-500" />
                  </div>
                </div>
              </div>
              <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-orange-700">
                      Ответ токены
                    </p>
                    <p className="text-2xl font-bold text-orange-900">
                      {overallStats.totals.completionTokens.toLocaleString()}
                    </p>
                  </div>
                  <div className="flex-shrink-0 ml-3">
                    <ArrowTrendingDownIcon className="h-6 w-6 text-orange-500" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Статистика за период */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            Статистика за период ({new Date(from).toLocaleDateString()} -{" "}
            {new Date(to).toLocaleDateString()})
          </h3>
          <div className="card">
            <div className="px-6 py-4">
              {!aiUsage ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  <span className="ml-2 text-sm text-gray-500">
                    Загрузка данных...
                  </span>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-500 h-10 flex items-start pt-1 leading-tight">
                            Запросов за период
                          </div>
                          <div className="text-2xl font-bold text-gray-900">
                            {aiUsage.totals.requests.toLocaleString()}
                          </div>
                        </div>
                        <div className="flex-shrink-0 ml-3">
                          <ChatBubbleLeftRightIcon className="h-6 w-6 text-blue-500" />
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-500 h-10 flex items-start pt-1 leading-tight">
                            Токенов за период
                          </div>
                          <div className="text-2xl font-bold text-gray-900">
                            {aiUsage.totals.totalTokens.toLocaleString()}
                          </div>
                        </div>
                        <div className="flex-shrink-0 ml-3">
                          <CpuChipIcon className="h-6 w-6 text-purple-500" />
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-500 h-10 flex items-start pt-1 leading-tight">
                            Промпт токены
                          </div>
                          <div className="text-2xl font-bold text-gray-900">
                            {aiUsage.totals.promptTokens.toLocaleString()}
                          </div>
                        </div>
                        <div className="flex-shrink-0 ml-3">
                          <ArrowTrendingUpIcon className="h-6 w-6 text-green-500" />
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-500 h-10 flex items-start pt-1 leading-tight">
                            Ответ токены
                          </div>
                          <div className="text-2xl font-bold text-gray-900">
                            {aiUsage.totals.completionTokens.toLocaleString()}
                          </div>
                        </div>
                        <div className="flex-shrink-0 ml-3">
                          <ArrowTrendingDownIcon className="h-6 w-6 text-orange-500" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {aiUsage.series.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">
                        График по периодам
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 gap-2">
                          {usageSeries.map((point, idx) => {
                            const maxTokens = Math.max(
                              ...usageSeries.map((p) => p.y2)
                            );
                            const width =
                              maxTokens > 0 ? (point.y2 / maxTokens) * 100 : 0;
                            return (
                              <div
                                key={idx}
                                className="flex items-center justify-between py-2"
                              >
                                <div className="text-sm font-medium text-gray-700 w-24">
                                  {point.x}
                                </div>
                                <div className="flex-1 mx-4">
                                  <div className="bg-gray-200 rounded-full h-3 relative">
                                    <div
                                      className="bg-gradient-to-r from-blue-400 to-purple-500 h-3 rounded-full transition-all duration-300"
                                      style={{ width: `${width}%` }}
                                    ></div>
                                  </div>
                                </div>
                                <div className="text-sm text-gray-600 text-right">
                                  <div className="font-medium">
                                    {point.y} запросов
                                  </div>
                                  <div className="text-xs">
                                    {point.y2.toLocaleString()} токенов
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {aiUsage.series.length > 0 && (
                    <div className="overflow-x-auto">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">
                        Детальная таблица
                      </h4>
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                              Период
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                              Запросы
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                              Всего токенов
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                              Промпт
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                              Ответ
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {aiUsage.series.map((s, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                {new Date(s.period).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {s.requests}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                {s.totalTokens.toLocaleString()}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                {s.promptTokens.toLocaleString()}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                {s.completionTokens.toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* AI Usage by User */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <UsersIcon className="h-5 w-5 text-indigo-600" />
            <h3 className="text-lg font-medium text-gray-900">
              Использование по пользователям
            </h3>
            <span className="text-sm text-gray-500">
              ({new Date(from).toLocaleDateString()} -{" "}
              {new Date(to).toLocaleDateString()})
            </span>
          </div>
          <div className="card">
            <div className="px-6 py-4">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                        Пользователь
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                        Запросы
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                        Всего токенов
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                        Промпт токены
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                        Ответ токены
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {byUser.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-8 text-center text-sm text-gray-500"
                        >
                          <CpuChipIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                          Нет данных за выбранный период
                        </td>
                      </tr>
                    ) : (
                      byUser.map((r, idx) => {
                        const maxTokens = Math.max(
                          ...byUser.map((u) => u.totalTokens)
                        );
                        const tokenPercentage =
                          maxTokens > 0 ? (r.totalTokens / maxTokens) * 100 : 0;
                        return (
                          <tr
                            key={r.userId || "null"}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-8 w-8">
                                  <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center">
                                    <span className="text-xs font-medium text-white">
                                      {(r.email || r.userId || "?")
                                        .charAt(0)
                                        .toUpperCase()}
                                    </span>
                                  </div>
                                </div>
                                <div className="ml-3">
                                  <div className="text-sm font-medium text-gray-900">
                                    {r.email ||
                                      r.userId ||
                                      "Неизвестный пользователь"}
                                  </div>
                                  {r.userId && (
                                    <div className="text-xs text-gray-500">
                                      ID: {r.userId.slice(0, 8)}...
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {r.requests}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center">
                                <span className="text-sm font-medium text-gray-900 mr-2">
                                  {r.totalTokens.toLocaleString()}
                                </span>
                                <div className="flex-1 bg-gray-200 rounded-full h-2 w-20">
                                  <div
                                    className="bg-gradient-to-r from-green-400 to-blue-500 h-2 rounded-full"
                                    style={{ width: `${tokenPercentage}%` }}
                                  ></div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                              {r.promptTokens.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                              {r.completionTokens.toLocaleString()}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
